import { useAppConstantsContext, AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useFamilyTreeReports,
  JsonPersonReport,
  JsonAncestorReport,
  JsonObituaryReport,
  formatBulkAddSubject,
  familySearchAddressParser,
  useAncestryTree,
  useCreateFamilyTree,
  formatFamilyTreeDataFromAncestorReport,
  useFocusedFamilyTreeMember,
} from '@ltvco/refresh-lib/fmt';
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Grid, Box, useTheme } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  useScreenSize,
} from '@ltvco/refresh-lib/utils';
import {
  AncestorFacts,
  AncestorOverview,
  type commonTypes,
  nameHasSpecialCharacters,
  peopleTypes,
  type Relative,
  ReportFactory,
  ReportLoading,
} from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchURLParams,
  AncestorRelationships,
  Memory,
  NullableAdvancedSearchURLParams,
  ReportMenuItem,
  ReportNavigationMobileMenu,
  AncestorMemories,
  AncestorMemoryModal,
  calculateAge,
  ReportNullState,
} from '@ltvco/refresh-lib/v2';
import {
  AccountCircle,
  Lightbulb,
  FamilyRestroom,
  Image,
} from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { REPORT_PROCESSORS, SEARCH_TYPES } from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';
import {
  GenericReportResponse,
  ProcessedAncestorReportData,
} from 'components/ChatBV/types';
import { ReportBannerContainer } from 'components/reports/SearchBanners/components';
import { ObituarySearchBanner } from 'components/reports/SearchBanners/ObituarySearchBanner';
import { PersonSearchBanner } from 'components/reports/SearchBanners/PersonSearchBanner';
import { ReportRouteProps } from 'components/reports/types';

interface AncestorReportProps extends ReportRouteProps {}

const FILTER_FACTS_TYPES = ['Death', 'Birth', 'Burial'];

export function AncestorChatBvReport({ permalink }: AncestorReportProps) {
  const theme = useTheme();
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const { isMobile } = useScreenSize();
  const queryResult = useReport(permalink);
  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();
  const [selectedMemory, setSelectedMemory] = useState<Memory>();
  const [selectedMemoryText, setSelectedMemoryText] = useState<string>('');
  const navigate = useNavigate();
  const { trackEventGA4 } = useContext(AppConfig);
  const { navigateToFocusedMember } = useFocusedFamilyTreeMember();
  const { sendPersonFollowUpMessage, messages } = useContext(ChatContext);

  const { isCreatingFamilyTree, createFamilyTree } = useCreateFamilyTree();

  const { data: reportsData, isLoading: isLoadingTreeReports } =
    useFamilyTreeReports({
      bodyOptions: {
        report_type: ['AncestorReport'],
        placeholders_excluded: true,
      },
      logNotFoundErrorEnabled: false,
    });

  const { data: familyTreeData } = useAncestryTree({
    enabledOnStart: true,
  });

  useScrollToSectionOnNavigate(hash, queryResult);

  useEffect(() => {
    if (queryResult?.data?.meta?.permalink) {
      const processedReport = REPORT_PROCESSORS[
        SEARCH_TYPES.ANCESTOR as keyof typeof REPORT_PROCESSORS
      ](queryResult.data as any, permalink);
      if (
        messages
          .map((message) => message.content)
          .includes(
            `Genealogy for ${
              (processedReport as ProcessedAncestorReportData)?.data
                ?.personName || ''
            }`
          )
      ) {
        return;
      }
      sendPersonFollowUpMessage(
        (processedReport as ProcessedAncestorReportData)?.data?.personName ||
          '',
        {
          reportData: queryResult.data as GenericReportResponse,
          type: SEARCH_TYPES.ANCESTOR,
          permalink,
        }
      );
    }
  }, [queryResult?.data?.meta?.permalink]);

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }

  const report = ReportFactory.create(queryResult?.data, 'person');
  const person = report?.data?.people?.[0];

  if (zeroed || !person) {
    return <ReportNullState />;
  }

  const familySearchId = person?.identity?.fs_ids![0];

  const reportInFamilyTree: boolean =
    reportsData?.reports?.some(
      (report: JsonAncestorReport | JsonPersonReport | JsonObituaryReport) =>
        'family_search_id' in report &&
        report.family_search_id === familySearchId
    ) ?? false;

  const memberIdInFamilyTree = reportInFamilyTree
    ? reportsData?.reports?.find((report) => {
        return (
          'family_search_id' in report &&
          report.family_search_id === familySearchId
        );
      })?.member_id
    : undefined;

  const fullName = person?.identity?.names?.[0]?.full;
  const primaryName = person?.identity?.names?.[0];
  const otherNames = person?.identity?.names
    ?.slice(1)
    ?.map((name: commonTypes.Name) => name.full)
    .join(', ');

  const dateOfBirth = person?.identity?.dobs?.[0]?.date;
  const dateOfDeath = person?.identity?.dods?.[0]?.date;
  const birthAddress = person?.facts?.find(
    (fact: commonTypes.Fact) => fact?.type?.toLowerCase() === 'birth'
  )?.place?.full;
  const deathAddress = person?.facts?.find(
    (fact: commonTypes.Fact) => fact?.type?.toLowerCase() === 'death'
  )?.place?.full;
  const burialData = person?.facts?.find(
    (fact: commonTypes.Fact) => fact?.type?.toLowerCase() === 'burial'
  );
  const addressInfo = person?.contact?.addresses?.[0];

  const parsedAddress = familySearchAddressParser(addressInfo?.full);

  const ancestorSearchParams: NullableAdvancedSearchURLParams = {
    fullName: fullName || '',
    fName: primaryName?.parsed?.first || '',
    lName: primaryName?.parsed?.last || '',
    city: parsedAddress?.city || '',
    state: parsedAddress?.state || '',
    country: parsedAddress?.country || '',
    birthMonth: dateOfBirth?.parsed?.month?.toString()?.padStart(2, '0') || '',
    birthYear: dateOfBirth?.parsed?.year?.toString() || '',
    deathMonth: dateOfDeath?.parsed?.month?.toString()?.padStart(2, '0') || '',
    deathYear: dateOfDeath?.parsed?.year?.toString() || '',
    location: addressInfo?.full || '',
  };

  ancestorSearchParams.age = calculateAge(
    ancestorSearchParams as AdvancedSearchURLParams
  );

  const dateOfBurial = burialData?.date;
  const burialAddress = burialData?.place?.full;
  const photos = person?.images || [];

  const facts = Array.isArray(person?.facts)
    ? person?.facts?.filter(
        (fact: commonTypes.Fact) =>
          !FILTER_FACTS_TYPES.includes(fact?.type || '')
      )
    : [];

  const memories = person?.rawData?.genealogy?.memories || [];

  document.title = `${fullName ?? 'Ancestor Report'} - ${brandName}`;

  const reportIndi = formatFamilyTreeDataFromAncestorReport(
    person,
    permalink,
    birthAddress || deathAddress || burialAddress || ''
  );

  const handleAddingRelativeToFamilyTree = async () => {
    trackEventGA4({
      event_name: 'report_element_engagement',
      report_type: 'ancestor',
      type: 'add_family',
      interacted_from: 'overview',
    });

    if (!familyTreeData) {
      if (!reportIndi) {
        return;
      }

      await createFamilyTree({
        indi: reportIndi,
      });

      return;
    }

    if (reportInFamilyTree) {
      navigateToFocusedMember(memberIdInFamilyTree as number);
      return;
    }

    localStorage.setItem(
      'familyTreeRelativeToAdd',
      JSON.stringify(
        formatFamilyTreeDataFromAncestorReport(
          person,
          permalink,
          birthAddress || deathAddress || burialAddress || ''
        )
      )
    );

    navigate('/family-tree');
  };

  const bulkAddSubject = formatBulkAddSubject(
    person,
    permalink,
    birthAddress || deathAddress || burialAddress || ''
  );

  const personRelatives =
    person?.rawData?.connections?.relatives?.filter(
      (relative: peopleTypes.RelativeConnection) =>
        !nameHasSpecialCharacters(relative?.identity?.names?.[0]?.full || '') &&
        relative?.identity?.fs_ids.length &&
        relative?.identity?.fs_ids[0] !== 'UNKNOWN'
    ) || [];

  const getAncestorNavLinkData = ({
    factsCount,
    relationshipsCount,
  }: {
    factsCount: number;
    relationshipsCount: number;
  }): ReportMenuItem[] => {
    return [
      {
        href: '#ancestor-overview',
        labelText: 'Overview',
        reportItemsCount: null,
        icons: [
          <AccountCircle
            key="overview-icon"
            sx={{ color: theme.palette.primary.main }}
            fontSize="large"
          />,
        ],
      },
      {
        href: '#ancestor-facts',
        labelText: 'Facts',
        reportItemsCount: factsCount,
        icons: [
          <Lightbulb
            key="facts-icon"
            sx={{ color: theme.palette.primary.main }}
            fontSize="small"
          />,
        ],
      },
      {
        href: '#ancestor-relationships',
        labelText: 'Relationships',
        reportItemsCount: relationshipsCount,
        icons: [
          <FamilyRestroom
            key="relationships-icon"
            sx={{ color: theme.palette.primary.main }}
            fontSize="small"
          />,
        ],
      },
      {
        href: '#ancestor-memories',
        labelText: 'Memories',
        reportItemsCount: memories.length,
        icons: [
          <Image
            key="memories-icon"
            sx={{ color: theme.palette.primary.main }}
            fontSize="small"
          />,
        ],
      },
    ];
  };

  const ancestorNavLinkData = getAncestorNavLinkData({
    factsCount: facts.length,
    relationshipsCount: personRelatives?.length || 0,
  });

  const onMemoryModalClose = () => {
    setSelectedMemory(undefined);
    setSelectedMemoryText('');
    setTimeout(() => {
      const el = document.getElementById('ancestor-memories');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 1000);
  };

  const onDocumentSelect = async (memory: Memory): Promise<void> => {
    const response = await fetch(memory.url);
    const text = await response.text();

    setSelectedMemoryText(text);
    setSelectedMemory(memory);
  };

  const isMemoryText = selectedMemory?.media_type.includes('text');

  if (selectedMemory && isMobile) {
    return (
      <AncestorMemoryModal
        memory={selectedMemory}
        onClose={() => onMemoryModalClose()}
        isPureText={isMemoryText}
        text={selectedMemoryText}
      />
    );
  }

  const yearOfDeath = dateOfDeath?.parsed?.year;
  const yearOfBirth = dateOfBirth?.parsed?.year;
  const showPersonBanner =
    (yearOfDeath && yearOfDeath >= 1960) ||
    (!yearOfDeath && yearOfBirth && yearOfBirth >= 1850);
  const showObituaryBanner =
    (yearOfDeath && yearOfDeath >= 2000) ||
    (!yearOfDeath && yearOfBirth && yearOfBirth >= 1900);

  return (
    <Box marginTop={0}>
      {selectedMemory && !isMobile && (
        <AncestorMemoryModal
          memory={selectedMemory}
          onClose={() => onMemoryModalClose()}
          isPureText={isMemoryText}
          text={selectedMemoryText}
        />
      )}
      <Grid size={{ sm: 12, md: 9, lg: 9 }} ml={0} width="100%">
        <AncestorOverview
          familySearchId={familySearchId || ''}
          permalink={permalink}
          fullName={fullName || ''}
          otherNames={otherNames || ''}
          dateOfBirth={dateOfBirth}
          birthAddress={birthAddress || ''}
          dateOfDeath={dateOfDeath}
          deathAddress={deathAddress || ''}
          dateOfBurial={dateOfBurial}
          burialAddress={burialAddress || ''}
          reportInFamilyTree={reportInFamilyTree}
          addRelativeToFamilyTree={handleAddingRelativeToFamilyTree}
          isCreatingFamilyTree={isCreatingFamilyTree}
          isLoading={isLoadingTreeReports}
          photos={photos}
        />
        <AncestorFacts facts={facts} permalink={permalink} />
        <AncestorRelationships
          relatives={personRelatives as unknown as Relative[]}
          ancestorReportsInTree={
            reportsData?.reports as JsonAncestorReport[] | undefined
          }
          bulkAddSubject={bulkAddSubject}
          hasFamilyTreeCreated={!!familyTreeData}
          reportIndi={reportIndi}
        />
        <AncestorMemories
          memories={memories as Memory[]}
          onMemorySelect={(memory: Memory) => setSelectedMemory(memory)}
          onDocumentSelect={(memory: Memory) => onDocumentSelect(memory)}
        />
        <ReportBannerContainer
          sx={{
            flexDirection: 'column',
            width: '100%',
            [theme.breakpoints.up('lg')]: {
              flexDirection: 'column',
            },
          }}
        >
          {/*
            Obituary Report Banner Logic:
            - Show if person died after 2000
            - Show if person has no death date AND was born after 1900
          */}
          {showObituaryBanner && (
            <ObituarySearchBanner searchParams={ancestorSearchParams} />
          )}
          {/*
              Person Report Banner Logic:
              - Show if person died after 1960
              - Show if person has no death date AND was born after 1850
            */}
          {showPersonBanner && (
            <PersonSearchBanner searchParams={ancestorSearchParams} />
          )}
        </ReportBannerContainer>
      </Grid>

      <ReportNavigationMobileMenu
        reportType="Ancestor Report"
        headerTitle={fullName || ''}
        menuItems={ancestorNavLinkData}
      />
    </Box>
  );
}
